/* eslint-disable @typescript-eslint/no-explicit-any */
import { FieldValues, useFormContext } from 'react-hook-form';
import {
  AutocompleteElementProps,
  AutocompleteElement as MuiAutocompleteElement,
} from 'react-hook-form-mui';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

export default function AsyncAutocompleteElement<
  TFieldValues extends FieldValues,
>({
  textFieldProps,
  autocompleteProps,
  name,
  control,
  showCheckbox,
  rules,
  required,
  multiple,
  matchId,
  label,
  api,
  fetchOnMount = false,
}: Omit<
  AutocompleteElementProps<
    TFieldValues,
    string | any,
    boolean | undefined,
    boolean | undefined
  >,
  'options'
> & { api: any; fetchOnMount?: boolean }) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<any[]>([]);
  const checkingLoaded = useRef(true);
  const loading = open && checkingLoaded.current;
  const formcontext = useFormContext();
  const { t } = useTranslation();
  const value: any = formcontext.watch(name) as any;

  React.useEffect(() => {
    let active = true;

    (async () => {
      if (active && api != undefined) {
        const response = await api();
        setOptions([...response.data]);
        checkingLoaded.current = false;
      }
    })();

    return () => {
      active = false;
    };
  }, [loading, fetchOnMount, api, open]);

  React.useEffect(() => {
    if (value) {
      const valuesArray = Array.isArray(value) ? value : [value];
      const newOptions = valuesArray.filter(
        (item) => item && !options.some((option) => option.id === item.id),
      );
      if (newOptions.length > 0) {
        setOptions((prevOptions) => [...prevOptions, ...newOptions]);
      }
    }
  }, [value]);

  return (
    <MuiAutocompleteElement
      textFieldProps={textFieldProps}
      autocompleteProps={{
        onOpen: () => {
          setOpen(true);
        },
        onClose: () => {
          setOpen(false);
        },
        open: open,
        loadingText: t('Loading...'),
        noOptionsText: t('None Option'),
        ...autocompleteProps,
      }}
      name={name}
      control={control}
      loading={loading}
      showCheckbox={showCheckbox}
      rules={rules}
      required={required}
      multiple={multiple}
      matchId={matchId}
      label={label}
      options={options}
    />
  );
}
